import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { formatCredits } from '../domains/assistant/formatCredits'
import type { AiCredits_WalletResponseDados } from '../contracts/AiCredits_WalletResponse'
import type { AiCredits_TopupInput } from '../contracts/AiCredits_TopupInput'
import type { AiCredits_LedgerInput } from '../contracts/AiCredits_LedgerInput'
import type { AiCredits_AdjustInput } from '../contracts/AiCredits_AdjustInput'

// Import side-effect: registers the contracts with the responseContract registry.
import '../contracts/AiCredits_WalletResponse'
import '../contracts/AiCredits_AdminOverviewResponse'
import '../contracts/AiCredits_LedgerResponse'
import '../contracts/AiCredits_TopupInput'
import '../contracts/AiCredits_TopupResponse'
import '../contracts/AiCredits_AdjustInput'
import '../contracts/AiCredits_AdjustResponse'

/**
 * useCreditWallet — SOLE network owner for ai/credits/* routes.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service),
 * never to agentHost. The `no_httpservice_in_vue` qa rule forbids
 * any fetch in .vue files — this composable is the single gateway.
 *
 * Module-scope singleton: T2 and T3 share one selectedFranquiaId /
 * balance instance (mirrors the useGlobalBanners module-scope ref
 * pattern). Call `_reset()` in afterEach for test isolation.
 */

/**
 * Fallback sentinel, used ONLY when the API does not carry a threshold.
 *
 * The authoritative value is `low_balance` / `low_balance_threshold_micro` in
 * the wallet response: the API derives it from the real price of a turn (S7.5,
 * 2026-07-29), which this constant cannot do — 500_000 micro was a flat guess
 * that ran ~63× under the ceiling and warned far too late. The fields are
 * nullable on purpose (S7-R05: so the API can ship after the front), and the
 * API reports null when there is no price row; that is the only case that still
 * falls back here.
 */
const CREDITS_LOW_THRESHOLD_MICRO = 500_000

/**
 * Conversion factor: 1 display credit = 1_000_000 micro-credits.
 * Use this in page-layer conversions (never a bare literal 1000000).
 */
export const MICRO_PER_CREDIT = 1_000_000

// ── Module-scope singleton state ────────────────────────────────────────────

const balance = ref<string>('')
const balanceMicro = ref<number>(0)
const state = ref<'idle' | 'loaded' | 'insufficient'>('idle')
// Low-balance flag for the ASSISTANT SIDEBAR notice (user decision 2026-08-12:
// credit warnings live inside the assistant; the global banner area is for
// subscription/grace notices only). Server-owned threshold when present.
const lowBalance = ref<boolean>(false)
const selectedFranquiaId = ref<number | null>(null)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

function applyWallet(dados: AiCredits_WalletResponseDados): void {
  balance.value = `${formatCredits(dados.balance_micro)} cr`
  balanceMicro.value = dados.balance_micro

  lowBalance.value = dados.low_balance
    ?? (dados.balance_micro < (dados.low_balance_threshold_micro ?? CREDITS_LOW_THRESHOLD_MICRO))
}

// ── Public composable ────────────────────────────────────────────────────────

export function useCreditWallet() {
  /**
   * fetchWallet — GET ai/credits/wallet/{franquiaId}.
   *
   * Sets `balance`, `balanceMicro`, and `selectedFranquiaId`.
   * On HttpError with status 402 sets state='insufficient' and does NOT
   * re-throw (the 402 path is an expected business condition, not a crash).
   * All other errors propagate to the caller.
   */
  async function fetchWallet(franquiaId: number): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl(`ai/credits/wallet/${franquiaId}`),
        { responseContract: 'AiCredits_WalletResponse', skipErrorDisplay: false },
      )

      // httpService returns the full {success, dados, error} envelope; the
      // responseContract validates the dados shape.
      const resp = raw as { dados: AiCredits_WalletResponseDados }
      selectedFranquiaId.value = franquiaId
      applyWallet(resp.dados)
      state.value = 'loaded'

      log('useCreditWallet', 'fetchWallet', 'Carteira carregada', {
        franquiaId,
        balance: balance.value,
      })
    } catch (err) {
      const httpErr = err as HttpError
      if (httpErr?.status === 402) {
        state.value = 'insufficient'
        log('useCreditWallet', 'fetchWallet', 'Saldo insuficiente (402)', { franquiaId })
        return // do NOT re-throw — caller reads state.value
      }
      log('useCreditWallet', 'fetchWallet', 'Erro ao carregar carteira', {
        franquiaId,
        message: (err as Error)?.message,
        status: httpErr?.status,
      })
      throw err
    }
  }

  /**
   * topup — POST ai/credits/topup.
   * Binds EXACTLY the AiCredits_TopupInput field names.
   */
  async function topup(input: AiCredits_TopupInput): Promise<void> {
    const body: AiCredits_TopupInput = {
      franquia_id: input.franquia_id,
      micro_credits: input.micro_credits,
      request_id: input.request_id,
      ...(input.note !== undefined ? { note: input.note } : {}),
    }

    await httpService.post(
      sistemaUrl('ai/credits/topup'),
      body,
      {
        inputContract: 'AiCredits_TopupInput',
        responseContract: 'AiCredits_TopupResponse',
        // A recusa chega em HTTP 200 com o envelope em success:false. Sem isto
        // o modal fecha dizendo "Créditos adicionados com sucesso" sobre uma
        // carteira que não recebeu nada.
        throwOnEnvelopeFailure: 'topup_failed',
      },
    )

    log('useCreditWallet', 'topup', 'Topup realizado', {
      franquiaId: input.franquia_id,
      microCredits: input.micro_credits,
    })
  }

  /**
   * adjust — POST ai/credits/adjust.
   * Full-only. Signed micro-credit delta (positive = credit, negative = debit).
   * Binds EXACTLY the AiCredits_AdjustInput field names.
   */
  async function adjust(input: AiCredits_AdjustInput): Promise<void> {
    const body: AiCredits_AdjustInput = {
      franquia_id: input.franquia_id,
      signed_micro: input.signed_micro,
      reason: input.reason,
      request_id: input.request_id,
    }

    await httpService.post(
      sistemaUrl('ai/credits/adjust'),
      body,
      {
        inputContract: 'AiCredits_AdjustInput',
        responseContract: 'AiCredits_AdjustResponse',
        throwOnEnvelopeFailure: 'adjust_failed',
      },
    )

    log('useCreditWallet', 'adjust', 'Ajuste realizado', {
      franquiaId: input.franquia_id,
      signedMicro: input.signed_micro,
    })
  }

  /**
   * fetchAdminOverview — GET ai/credits/admin/overview.
   * Staff console only (Full-gated). Cost fields live ONLY here.
   */
  async function fetchAdminOverview() {
    return httpService.get(
      sistemaUrl('ai/credits/admin/overview'),
      { responseContract: 'AiCredits_AdminOverviewResponse' },
    )
  }

  /**
   * fetchLedger — GET ai/credits/admin/ledger.
   * Staff console only (Full-gated).
   */
  async function fetchLedger(input?: AiCredits_LedgerInput) {
    const params = input
      ? new URLSearchParams(
          Object.fromEntries(
            Object.entries(input)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)]),
          ),
        ).toString()
      : ''

    const url = params
      ? sistemaUrl(`ai/credits/admin/ledger?${params}`)
      : sistemaUrl('ai/credits/admin/ledger')

    return httpService.get(url, { responseContract: 'AiCredits_LedgerResponse' })
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    balance.value = ''
    balanceMicro.value = 0
    state.value = 'idle'
    lowBalance.value = false
    selectedFranquiaId.value = null
  }

  return {
    // reactive state
    balance,
    balanceMicro,
    state,
    lowBalance,
    selectedFranquiaId,
    // methods
    fetchWallet,
    topup,
    adjust,
    fetchAdminOverview,
    fetchLedger,
    _reset,
  }
}
